/**
 * House of Places — world map.
 *
 * No d3, no topojson, no CDN: geometry is pre-projected (Natural Earth I) into
 * assets/map-geo.json at build time and fetched async. This file only draws
 * paths, pans/zooms, searches, and routes clicks.
 *
 * Availability is data-driven: the section inlines a JSON map of collection
 * titles -> URLs. A country whose lowercased name matches a
 * collection is "available": tinted differently and clicking it opens the
 * collection. Anything else opens the themed request dialog.
 */
(function () {
  /* One set of listeners per map section, removable. The theme editor (and
     the dev server's hot reload) swaps the section's markup in place; the
     old listeners kept driving the OLD, detached tooltip while the new one
     stayed wherever it was — a box that neither followed nor closed. Each
     section now binds with an AbortSignal and is torn down on unload and
     rebuilt on load. */
  function kur(root) {
  if (root.hopMapKapat) return;
  var denetci = new AbortController();
  var sinyal = denetci.signal;
  root.hopMapKapat = function () {
    denetci.abort();
    root.hopMapKapat = null;
  };

  var svg = root.querySelector('svg');
  var group = svg.querySelector('g');
  var wrap = root.querySelector('[data-hop-map-viewport]');
  var dialog = root.querySelector('dialog');
  var searchInput = root.querySelector('[data-hop-map-search]');
  var sugBox = root.querySelector('[data-hop-map-suggestions]');
  var hint = root.querySelector('[data-hop-map-zoom-level]');

  var available = {};
  try {
    available = JSON.parse(root.querySelector('[data-hop-map-available]').textContent);
  } catch (e) {
    /* empty map is fine */
  }

  var strings = {};
  try {
    strings = JSON.parse(root.querySelector('[data-hop-map-strings]').textContent);
  } catch (e) {}

  // Cities are search-only: the map geometry carries none, so they never get
  // an element and are never painted. They exist so that typing a city name
  // finds the poster instead of returning "try another place name".
  var cities = [];
  try {
    cities = Object.values(
      JSON.parse(root.querySelector('[data-hop-map-cities]').textContent)
    );
  } catch (e) {
    /* no cities yet — the country search still works */
  }

  var places = []; // {name, el, availableUrl}

  function slot(name) {
    return available[name.toLowerCase()] || null;
  }

  // One spelling for matching: accents and the Turkish dotted İ fold away
  // ("İstanbul" lower-cases to "i̇stanbul" and matched nothing), runs of
  // spaces collapse. The squeezed form lets "newyork" find "New York".
  function norm(s) {
    return String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function eslesir(ad, q) {
    var a = norm(ad);
    return a.indexOf(q) !== -1 || a.replace(/ /g, '').indexOf(q.replace(/ /g, '')) !== -1;
  }


  function draw(geo) {
    svg.setAttribute('viewBox', geo.viewBox);
    var frag = document.createDocumentFragment();

    function add(item, cls) {
      var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      var hit = slot(item.name);
      // A demo row is a placeholder the merchant typed in while the real place
      // collections do not exist yet. It must not wear the "posters available"
      // tint — that would have the map promising stock the shop does not hold.
      // It stays clickable (the click opens a search) but reads as not
      // illustrated, which is the truth.
      var gercek = hit && !hit.demo;
      p.setAttribute('d', item.d);
      p.setAttribute('class', cls + (gercek ? ' is-available' : ''));
      p.setAttribute('data-name', item.name);
      p.setAttribute('tabindex', '0');
      p.setAttribute('role', 'button');
      p.setAttribute('aria-label', item.name);
      frag.appendChild(p);
      places.push({
        name: item.name,
        el: p,
        url: hit ? hit.url : null,
        kind: cls.indexOf('state') > -1 ? 'state' : 'country',
      });
    }

    geo.countries.forEach(function (c) {
      add(c, 'hop-map__country');
    });
    group.appendChild(frag);
  }

  /* ---------------- pan / zoom ---------------- */
  var k = 1,
    tx = 0,
    ty = 0;
  var K_MIN = 1,
    K_MAX = 12;

  function apply() {
    group.setAttribute('transform', 'translate(' + tx + ',' + ty + ') scale(' + k + ')');
    group.style.setProperty('--k', k);
    if (hint && armed) hint.textContent = Math.round(k * 100) + '%';
    svg.classList.toggle('is-zoomed', k > 1.01);
  }

  function clientToView(cx, cy) {
    var pt = new DOMPoint(cx, cy).matrixTransform(svg.getScreenCTM().inverse());
    return [pt.x, pt.y];
  }

  function zoomAt(cx, cy, factor) {
    var nk = Math.min(K_MAX, Math.max(K_MIN, k * factor));
    factor = nk / k;
    var v = clientToView(cx, cy);
    tx = v[0] - factor * (v[0] - tx);
    ty = v[1] - factor * (v[1] - ty);
    k = nk;
    if (k === K_MIN) {
      tx = 0;
      ty = 0;
    }
    apply();
  }

  // Scroll-jacking guard: the wheel only zooms after the map has been clicked.
  // Clicking anywhere outside hands the wheel back to the page; the pan/zoom
  // state itself is kept — only the wheel lock changes.
  var armed = false;
  // No wheel on a phone: "click to enable scroll zoom" would be a lie there.
  var dokunmatik = window.matchMedia('(hover: none)').matches;

  function arm() {
    if (armed) return;
    armed = true;
    root.classList.add('is-armed');
    if (hint) hint.textContent = Math.round(k * 100) + '%';
  }

  function disarm() {
    if (!armed) return;
    armed = false;
    root.classList.remove('is-armed');
    if (hint) hint.textContent = dokunmatik ? Math.round(k * 100) + '%' : strings.zoom_lock || '';
  }

  document.addEventListener(
    'pointerdown',
    function (e) {
      if (!root.contains(e.target)) disarm();
    },
    { signal: sinyal },
  );

  wrap.addEventListener(
    'wheel',
    function (e) {
      if (!armed) return; // let the page scroll
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.25 : 0.8);
    },
    { passive: false },
  );

  var drag = null;
  svg.addEventListener('pointerdown', function (e) {
    arm();
    var t0 = root.querySelector('[data-hop-map-tooltip]');
    if (t0) t0.hidden = true;
    // Capture retargets every later event to the svg, so the clicked path must
    // be remembered NOW — on pointerup e.target is no longer the path.
    drag = {
      x: e.clientX,
      y: e.clientY,
      tx: tx,
      ty: ty,
      moved: false,
      hit: e.target.closest('path[data-name]'),
    };
    svg.setPointerCapture(e.pointerId);
  });
  svg.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var ctm = svg.getScreenCTM();
    var dx = (e.clientX - drag.x) / ctm.a;
    var dy = (e.clientY - drag.y) / ctm.d;
    if (Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y) > 4) drag.moved = true;
    tx = drag.tx + dx;
    ty = drag.ty + dy;
    apply();
  });
  svg.addEventListener('pointerup', function (e) {
    var d = drag;
    drag = null;
    if (svg.hasPointerCapture(e.pointerId)) svg.releasePointerCapture(e.pointerId);
    if (!d || d.moved) return;
    if (d.hit) activate(d.hit.getAttribute('data-name'));
  });
  // On touch screens a vertical swipe belongs to the page (touch-action:
  // pan-y in world-map.css); the browser takes it over and cancels the pointer.
  svg.addEventListener('pointercancel', function (e) {
    drag = null;
    if (svg.hasPointerCapture(e.pointerId)) svg.releasePointerCapture(e.pointerId);
  });
  svg.addEventListener('keydown', function (e) {
    if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('path[data-name]')) {
      e.preventDefault();
      activate(e.target.getAttribute('data-name'));
    }
  });

  root.querySelectorAll('[data-hop-map-zoom]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      arm();
      var r = wrap.getBoundingClientRect();
      var op = btn.getAttribute('data-hop-map-zoom');
      if (op === 'reset') {
        k = 1;
        tx = 0;
        ty = 0;
        apply();
      } else {
        zoomAt(r.left + r.width / 2, r.top + r.height / 2, op === 'in' ? 1.5 : 1 / 1.5);
      }
    });
  });

  // The US used to split into its 51 states on click; the client asked for
  // one country shape like every other, so it now behaves like the rest.
  function flyTo(el) {
    var b = el.getBBox();
    flyToBox(b);
  }

  function flyToBox(b) {
    var vb = svg.viewBox.baseVal;
    var nk = Math.min(K_MAX, 0.6 * Math.min(vb.width / b.width, vb.height / b.height));
    k = Math.max(K_MIN, nk);
    tx = vb.width / 2 - k * (b.x + b.width / 2);
    ty = vb.height / 2 - k * (b.y + b.height / 2);
    apply();
  }

  /* ---------------- click routing ---------------- */
  function activate(name) {
    var hit = slot(name);
    if (hit) {
      window.location.href = hit.url;
      return;
    }
    openRequest(name);
  }

  function openRequest(name) {
    if (!dialog) return;
    dialog.querySelector('[data-hop-map-dialog-title]').textContent = name || strings.anywhere || '';
    // A clicked place is the title; the eyebrow then says what the box is.
    // From the footer button the title already says it, so no eyebrow.
    var eyebrow = dialog.querySelector('[data-hop-map-dialog-eyebrow]');
    if (eyebrow) {
      eyebrow.textContent = name ? strings.anywhere || '' : '';
      eyebrow.hidden = !name;
    }
    dialog.querySelector('[data-hop-map-dialog-text]').textContent = (strings.request_text || '').replace(
      '%{name}',
      name || strings.anywhere || '',
    );
    var placeField = dialog.querySelector('[data-hop-map-dialog-place]');
    if (placeField) placeField.value = name || '';
    dialog.showModal();
  }

  // Back from a send: the form's result is in the dialog; show it.
  if (dialog && dialog.querySelector('[data-hop-map-dialog-status]')) {
    openRequest('');
  }

  // An empty collection links here with #suggest-place: open the form.
  if (window.location.hash === '#suggest-place') {
    openRequest('');
  }

  root.querySelectorAll('[data-hop-map-request]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      openRequest('');
    });
  });

  /* ---------------- search ---------------- */
  function renderSuggestions(q) {
    if (!sugBox) return;
    sugBox.innerHTML = '';
    q = norm(q);
    if (!q) {
      sugBox.hidden = true;
      return;
    }
    // Cities first: someone typing "istanbul" wants the poster, not Turkey.
    var cityHits = cities
      .filter(function (c) {
        return eslesir(c.name, q);
      })
      .map(function (c) {
        return { name: c.name, url: c.url, kind: 'city', country: c.country };
      });

    var hits = cityHits
      .concat(
        places
          .filter(function (p) {
            return eslesir(p.name, q);
          })
          .sort(function (a, b) {
            return (b.url ? 1 : 0) - (a.url ? 1 : 0) || a.name.localeCompare(b.name);
          })
      )
      .slice(0, 8);
    if (!hits.length) {
      var d = document.createElement('div');
      d.className = 'hop-map__sug hop-map__sug--none';
      d.textContent = strings.no_results || '';
      sugBox.appendChild(d);
    }
    hits.forEach(function (p) {
      var d = document.createElement('button');
      d.type = 'button';
      d.className = 'hop-map__sug' + (p.url ? ' is-available' : '');
      d.textContent = p.name;
      var kindEl = document.createElement('span');
      kindEl.className = 'hop-map__sug-type';
      if (p.kind === 'city') {
        // The country reads as the useful label here: "Istanbul — Turkey".
        kindEl.textContent = p.country || '';
      } else {
        kindEl.textContent = p.kind === 'state' ? strings.type_state || '' : strings.type_country || '';
      }
      d.appendChild(kindEl);
      d.addEventListener('click', function () {
        sugBox.hidden = true;
        searchInput.value = p.name;
        // A city has no shape on the map to fly to; it goes straight to its
        // own collection, filtered to that city.
        if (p.kind === 'city') {
          if (p.url) window.location.href = p.url;
          return;
        }
        flyTo(p.el);
        activate(p.name);
      });
      sugBox.appendChild(d);
    });
    sugBox.hidden = false;
  }

  function pickSelected() {
    if (!sugBox || sugBox.hidden) return;
    var sel =
      sugBox.querySelector('.hop-map__sug.is-sel') || sugBox.querySelector('.hop-map__sug:not(.hop-map__sug--none)');
    if (sel) sel.click();
  }

  if (searchInput) {
    searchInput.addEventListener('input', function () {
      renderSuggestions(searchInput.value);
    });
    searchInput.addEventListener('keydown', function (e) {
      if (!sugBox || sugBox.hidden) return;
      var items = Array.prototype.slice.call(sugBox.querySelectorAll('.hop-map__sug:not(.hop-map__sug--none)'));
      if (!items.length) return;
      var i = items.findIndex(function (x) {
        return x.classList.contains('is-sel');
      });
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (i > -1) items[i].classList.remove('is-sel');
        i = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
        items[i].classList.add('is-sel');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        pickSelected();
      } else if (e.key === 'Escape') {
        sugBox.hidden = true;
      }
    });
    var searchBtn = root.querySelector('[data-hop-map-search-btn]');
    if (searchBtn) {
      searchBtn.addEventListener('click', function () {
        renderSuggestions(searchInput.value);
        pickSelected();
      });
    }
    searchInput.addEventListener('blur', function () {
      setTimeout(function () {
        if (sugBox) sugBox.hidden = true;
      }, 200);
    });
  }

  /* ---------------- hover tooltip ---------------- */
  /* Strictly a hover state (client, 2026-10-02): the box is visible ONLY
     while the pointer is over that country, and closes the moment it is not.

     Earlier versions relied on pointerleave/pointermove alone. Those miss
     exits that produce no further event inside the page — leaving the
     window over the edge of this full-bleed map, onto the scrollbar, or into
     another app — and the box stayed open over a country nobody was
     pointing at (client screenshot: China). Three layers now:
       1. pointerover / pointerout on the country paths decide what is
          hovered; leaving a path closes the box at once.
       2. Every way of leaving the page (window mouseout with no target,
          blur, tab hidden, scroll, wheel, press, drag) closes it.
       3. While it is open, a watchdog asks the browser whether that path is
          still :hover and closes the box if not — whatever event was lost. */
  var tip = root.querySelector('[data-hop-map-tooltip]');
  if (tip) {
    // A short rest before the box appears, so sweeping across the map does
    // not flash a box over every country on the way.
    var BEKLEME = 140;
    // Within one big country the box stays put until the pointer has moved
    // this far from where it appeared, then jumps to the pointer — it never
    // chases the cursor pixel by pixel, and never lingers far from it.
    var KAYMA = 60;
    var gecikme = null;
    var bekci = null;
    var uzerinde = null; // the path under the pointer, or null
    var acik = null; // the path whose box is showing
    var capa = { x: 0, y: 0 };
    var son = { x: 0, y: 0 };

    function hideTip() {
      clearTimeout(gecikme);
      gecikme = null;
      clearInterval(bekci);
      bekci = null;
      acik = null;
      tip.hidden = true;
    }

    function yerlestir(x, y) {
      var r = wrap.getBoundingClientRect();
      tip.style.left = Math.max(8, Math.min(x - r.left + 14, r.width - tip.offsetWidth - 8)) + 'px';
      tip.style.top = Math.max(8, Math.min(y - r.top + 14, r.height - tip.offsetHeight - 8)) + 'px';
      capa = { x: x, y: y };
    }

    function goster(path) {
      if (drag || path !== uzerinde || !path.isConnected) return;
      var name = path.getAttribute('data-name');
      var hit = slot(name);
      tip.querySelector('[data-hop-map-tooltip-name]').textContent = name;
      var status = tip.querySelector('[data-hop-map-tooltip-status]');
      status.textContent = hit ? strings.tooltip_available || '' : strings.tooltip_request || '';
      status.classList.toggle('is-available', !!hit);
      tip.hidden = false;
      yerlestir(son.x, son.y);
      acik = path;
      clearInterval(bekci);
      bekci = setInterval(function () {
        if (!acik || !acik.isConnected || !acik.matches(':hover') || uzerinde !== acik) hideTip();
      }, 150);
    }

    svg.addEventListener('pointerover', function (e) {
      if (e.pointerType === 'touch') return;
      var path = e.target.closest && e.target.closest('path[data-name]');
      if (!path || path === uzerinde) return;
      hideTip();
      uzerinde = path;
      son = { x: e.clientX, y: e.clientY };
      if (drag) return;
      gecikme = setTimeout(function () {
        goster(path);
      }, BEKLEME);
    });

    svg.addEventListener('pointerout', function (e) {
      var path = e.target.closest && e.target.closest('path[data-name]');
      if (!path) return;
      var gidilen = e.relatedTarget && e.relatedTarget.closest ? e.relatedTarget.closest('path[data-name]') : null;
      if (gidilen === path) return;
      if (uzerinde === path) uzerinde = null;
      hideTip();
    });

    svg.addEventListener('pointermove', function (e) {
      son = { x: e.clientX, y: e.clientY };
      if (drag) {
        hideTip();
        return;
      }
      if (acik && Math.abs(e.clientX - capa.x) + Math.abs(e.clientY - capa.y) > KAYMA) yerlestir(e.clientX, e.clientY);
    });

    // Anything that is not a country under the pointer closes it.
    document.addEventListener(
      'pointermove',
      function (e) {
        if (!e.target.closest || !e.target.closest('path[data-name]')) {
          uzerinde = null;
          hideTip();
        }
      },
      { capture: true, signal: sinyal },
    );
    // Leaving the window: mouseout with no element to go to.
    window.addEventListener(
      'mouseout',
      function (e) {
        if (!e.relatedTarget) {
          uzerinde = null;
          hideTip();
        }
      },
      { signal: sinyal },
    );
    document.documentElement.addEventListener('mouseleave', hideTip, { signal: sinyal });
    svg.addEventListener('pointerdown', hideTip);
    svg.addEventListener('wheel', hideTip, { passive: true });
    window.addEventListener('scroll', hideTip, { passive: true, signal: sinyal });
    window.addEventListener('blur', hideTip, { signal: sinyal });
    document.addEventListener('visibilitychange', hideTip, { signal: sinyal });
    sinyal.addEventListener('abort', hideTip);
  }

  /* ---------------- boot ---------------- */
  fetch(root.getAttribute('data-geo-url'))
    .then(function (r) {
      if (!r.ok) throw new Error('geo ' + r.status);
      return r.json();
    })
    .then(function (geo) {
      draw(geo);
      apply();
      if (hint) hint.textContent = dokunmatik ? '100%' : strings.zoom_lock || '';
    })
    .catch(function () {
      // No geometry, no map — but search (cities, request dialog) still works,
      // and the section says so instead of showing an empty blue box.
      root.classList.add('is-geo-failed');
    });
  }

  document.querySelectorAll('[data-hop-map]').forEach(kur);

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-hop-map]').forEach(kur);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-hop-map]').forEach(function (r) {
      if (r.hopMapKapat) r.hopMapKapat();
    });
  });
})();
